/** Lives in `components/`, not `tools/`: a `tools/` home made it a cross-tool importer (QUAL-NO-CROSS-TOOL-IMPORTS-001), while a component importing a tool is fine. */

import { resolvePropertyListPrice } from '@federa/utils/property/resolve-transaction';
import { extractPropertyImageUrls } from '@federa/components/lib/property-image-urls';
import { cleanAddress, parseAddress } from '@federa/components/lib/address-parsing';
import { resolvePropertyAddressWithDefault } from '@federa/utils/property/resolve-property-address-with-default';
import { addressNeedsParsing } from '@federa/utils/property/address-needs-parsing';
import { resolveHighestBidAmount } from '@federa/utils/property/resolve-highest-bid-amount';
import type { PropertyCardProps } from '@federa/types/ui-property-card';
import type { PropertyCardAddress, Property } from '@federa/types/properties';

function buildInitialPropertyCardAddress(details: Property['propertyDetails'], cleanedAddress: string): PropertyCardAddress {
	return {
		street: cleanedAddress,
		city: details.city ?? '',
		state: details.state ?? '',
		zipCode: details.zipCode ?? '',
	};
}

/** Resolve the auction flag, falling back to the matching active transaction. */
function extractIsAuction(property: Property): boolean {
	if (property.auction1 !== undefined) {
		return property.auction1;
	}
	return property.matchingActiveTransaction?.auction1 ?? false;
}

/** Resolve the current bid, preferring the highest bid amount then the last price. */
function extractCurrentBid(property: Property): number | undefined {
	const amount = resolveHighestBidAmount(property);
	if (amount > 0) return amount;
	return property.last;
}

/**
 * Map ALREADY-STRUCTURED property details onto the card's numeric detail fields with defaults. Not the shared
 * `extractPropertyDetails` (`apps/federa/src/lib/seo/property-text-extraction.ts`), which PARSES those numbers out of free-text
 * description prose -- opposite direction, different input.
 */
function mapCardDetailFields(details: Property['propertyDetails']): {
	bedrooms: number;
	bathrooms: number;
	squareFootage: number;
	acreage: number | undefined;
	yearBuilt: number | undefined;
} {
	return {
		bedrooms: details.bedrooms ?? 0,
		bathrooms: details.bathrooms ?? 0,
		squareFootage: details.squareFootage ?? 0,
		acreage: details.lotSize,
		yearBuilt: details.yearBuilt,
	};
}

function parseAddressToData(cleanedAddress: string): PropertyCardAddress {
	const parsed = parseAddress(cleanedAddress);
	return {
		street: parsed.street,
		city: parsed.city,
		state: parsed.state,
		zipCode: parsed.zipCode ?? '',
	};
}

/** Resolve property card address, parsing if necessary */
function resolvePropertyCardAddress(property: Property): PropertyCardAddress {
	const rawAddress = resolvePropertyAddressWithDefault(property, '');
	const cleanedAddress = cleanAddress(rawAddress);
	const initialData = buildInitialPropertyCardAddress(property.propertyDetails, cleanedAddress);

	if (addressNeedsParsing(initialData)) {
		return parseAddressToData(cleanedAddress);
	}
	// The resolved address is usually the FULL address while the card renders city/state on its own line, so the bold street line keeps only the street part.
	const parsedStreet = parseAddress(cleanedAddress).street;
	return { ...initialData, street: parsedStreet || cleanedAddress };
}

/** The price inputs a card's display resolves from its PROJECTION (`getPropertyPriceDisplay` adds the countdown):
 *  read by the interactive card and by the explore page's server-rendered preview card, so both price one card alike. */
export function resolveCardPriceInfo(card: PropertyCardProps['property']): { isAuction: boolean; highestBidAmount: number; listPrice: number } {
	const isAuction = card.isAuction ?? card.matchingActiveTransaction?.auction1 ?? false;
	const highestBidAmount = resolveHighestBidAmount(card);
	const listPrice = card.matchingActiveTransaction?.listPrice ?? card.listPrice;
	return { isAuction, highestBidAmount, listPrice };
}

export function transformPropertyToCardData(property: Property): PropertyCardProps['property'] {
	const addressData = resolvePropertyCardAddress(property);
	const detailsData = mapCardDetailFields(property.propertyDetails);

	return {
		id: property.id,
		images: extractPropertyImageUrls(property.imagesList),
		listPrice: resolvePropertyListPrice(property),
		address: addressData.street,
		city: addressData.city,
		state: addressData.state,
		zipCode: addressData.zipCode,
		bedrooms: detailsData.bedrooms,
		bathrooms: detailsData.bathrooms,
		squareFootage: detailsData.squareFootage,
		acreage: detailsData.acreage,
		yearBuilt: detailsData.yearBuilt,
		isAuction: extractIsAuction(property),
		currentBid: extractCurrentBid(property),
		auctionEndUtc: property.auctionEndUtc ?? null,
		highestBid: property.highestBid ?? null,
		// Copied explicitly. The card reads the bid through resolveHighestBidAmount, and this projection is the only
		// thing standing between the store and that call -- a field left out here cannot be recovered downstream.
		highestBidAmount: property.highestBidAmount ?? null,
		matchingActiveTransaction: property.matchingActiveTransaction ?? null,
	};
}
